import { ErrorCodeType } from "application/models/IError";
import { ICashlessRepository } from "domains/cashless/interfaces/ICashlessRepository";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { IOrderRepository } from "domains/order/interfaces/repositories/IOrderRepository";
import { IOrder } from "modules/order/domain/models/IOrder";
import { IPedidoPosDTO } from "modules/order/domain/models/IPedidoPosDTO";
import { RechargeType } from "modules/order/presentation/UseOrderProvider";
import { TransactionType } from "modules/payment/domain/models/TransactionType";
import { GetError } from "utils/GetError";
import { v4 } from "uuid";

export interface ISyncOrderResult {
  message?: string;
  success?: boolean;
  pedidoPOS?: IPedidoPosDTO;
}

export const SyncOrderUseCase = async (
  orderRepository: IOrderRepository,
  cashlessRepository: ICashlessRepository,
  order: IOrder,
  transactionType: TransactionType,
  kiosk: Kiosk,
  recharge?: RechargeType
): Promise<ISyncOrderResult> => {
  let result: ISyncOrderResult = {};
  if (order.totalValue === 0) {
    try {
      const pedidoPOS = await orderRepository.sendFreeOrdersAsync(
        order,
        kiosk.localId,
        transactionType,
        order.payment?.FinancialTransactionModel
      );
      result = {
        message: `Pedido sincronizado tag: ${
          pedidoPOS.clienteEstabelecimentoObject?.tag ?? "-"
        }`,
        success: true,
        pedidoPOS,
      };
      return result;
    } catch (error) {
      GetError(
        error,
        "onSucessPayment",
        "SyncOrderUseCase",
        "Falha ao sincronizar pedido",
        ErrorCodeType.ORDER
      );
      result = {
        message: `${
          (error as any)?.response?.data?.message ??
          "Falha ao sincronizar o pedido"
        }`,
        success: false,
      };
      return result;
    }
  }

  if (!recharge) {
    try {
      const pedidoPOS = await orderRepository.sendOrderAsync(
        order,
        order.payment?.paymentMethod ?? TransactionType.misto,
        order.payment?.FinancialTransactionModel
      );
      result = {
        message: `Pedido adicionado a fila de sincronização`,
        success: false,
        pedidoPOS,
      };

      return result;
    } catch (error) {
      GetError(
        error,
        "SyncOrderUseCase",
        "SyncOrderUseCase",
        "Falha ao sincronizar pedido",
        ErrorCodeType.GENERAL
      );

      result = {
        message: `Falha na sincronização`,
        success: false,
      };
      return result;
    }
  }

  if (recharge === "Payment") {
    try {
      await cashlessRepository.payAccount({
        accountId: order.customer?.associationId ?? "",
        amount: order.totalValue,
        discount: 0,
        discountOperator: "",
        discountReason: "",
        equipamentId: kiosk.id,
        equipmentName: kiosk.kioskName,
        installments: 1,
        localId: kiosk.localId,
        operatorId: null,
        operatorName: null,
        orderId: order.id,
        paymentId: v4(),
        paymentType: transactionType,
        serviceFeeEnabled: kiosk.serviceRate,
        success: true,
        transactionResponse:
          transactionType === TransactionType.pix
            ? JSON.stringify(order?.paymentPix)
            : JSON.stringify(order?.payment?.FinancialTransactionModel),
      });
      result = {
        message: "Pagamento sincronizado com sucesso",
        success: true,
      };
      return result;
    } catch (error) {
      GetError(
        error,
        "SyncOrderUseCase",
        "SyncOrderUseCase",
        "Falha ao sincronizar pedido",
        ErrorCodeType.GENERAL
      );

      result = {
        message: `Falha ao sincronizar pagamento`,
        success: false,
      };
      return result;
    }
  }

  if (recharge && recharge === "Recharge") {
    try {
      await cashlessRepository.rechargeAccount({
        localId: kiosk.localId,
        accountId: order.customer?.associationId ?? "",
        operatorId: null,
        equipamentId: kiosk.id,
        orderId: order.id,
        payments: [
          {
            amount: order.totalValue,
            discount: 0,
            installments: 1,
            paymentType: transactionType,
            transactionResponse:
              transactionType === TransactionType.pix
                ? JSON.stringify(order?.paymentPix)
                : JSON.stringify(order?.payment?.FinancialTransactionModel),
            success: true,
            hasDiscountInAmount: false,
          },
        ],
        meepUserId: "",
        rechargeMotive: "Recharga Totem",
        rechargeValue: order.totalValue,
        currentSessionId: order.currentSessionId,
      });
      result = {
        message: "Pagamento sincronizado com sucesso",
        success: true,
      };
      return result;
    } catch (error) {
      GetError(
        error,
        "SyncOrderUseCase",
        "SyncOrderUseCase",
        "Falha ao sincronizar pedido",
        ErrorCodeType.GENERAL
      );

      result = {
        message: `Falha ao sincronizar pagamento`,
        success: false,
      };
      return result;
    }
  }

  GetError(
    "Falha na sincronização",
    "SyncOrderUseCase",
    "SyncOrderUseCase",
    "Falha ao sincronizar pedido",
    ErrorCodeType.GENERAL
  );

  result = {
    message: `Falha na sincronização`,
    success: false,
  };
  return result;
};
